/** Small ports of Unity / game utility types used by the simulation (DOM-free). */

export const FIXED_DT = 1 / 120;

export interface V2 {
  x: number;
  y: number;
}

export const v2 = (x = 0, y = 0): V2 => ({ x, y });
export const vlen = (v: V2) => Math.hypot(v.x, v.y);
export const vdot = (a: V2, b: V2) => a.x * b.x + a.y * b.y;
export function vnorm(v: V2): V2 {
  const l = vlen(v);
  // Unity Vector2.normalized returns zero below 1e-5
  return l > 1e-5 ? { x: v.x / l, y: v.y / l } : { x: 0, y: 0 };
}

/** Vector2.Angle in degrees (unsigned). */
export function vangle(a: V2, b: V2): number {
  const d = Math.sqrt((a.x * a.x + a.y * a.y) * (b.x * b.x + b.y * b.y));
  if (d < 1e-15) return 0;
  const c = Math.max(-1, Math.min(1, (a.x * b.x + a.y * b.y) / d));
  return (Math.acos(c) * 180) / Math.PI;
}

export const sign = (n: number) => (n > 0 ? 1 : n < 0 ? -1 : 0);
/** Mathf.Sign (returns 1 for 0) */
export const mathfSign = (n: number) => (n >= 0 ? 1 : -1);
export const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
export const clamp01 = (v: number) => clamp(v, 0, 1);
export const lerp = (a: number, b: number, t: number) => a + (b - a) * clamp01(t);

export function moveTowards(cur: number, target: number, maxDelta: number): number {
  if (Math.abs(target - cur) <= maxDelta) return target;
  return cur + sign(target - cur) * maxDelta;
}

export function moveTowardsV(cur: V2, target: V2, maxDelta: number): V2 {
  const dx = target.x - cur.x, dy = target.y - cur.y;
  const d = Math.hypot(dx, dy);
  if (d <= maxDelta || d === 0) return { x: target.x, y: target.y };
  return { x: cur.x + (dx / d) * maxDelta, y: cur.y + (dy / d) * maxDelta };
}

/** Unity Mathf.SmoothDamp (scalar). `vel` is an in/out box. */
export function smoothDamp(current: number, target: number, vel: { v: number }, smoothTime: number, maxSpeed = Infinity, deltaTime = FIXED_DT): number {
  smoothTime = Math.max(0.0001, smoothTime);
  const omega = 2 / smoothTime;
  const x = omega * deltaTime;
  const exp = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);
  let change = current - target;
  const originalTo = target;
  const maxChange = maxSpeed * smoothTime;
  change = clamp(change, -maxChange, maxChange);
  target = current - change;
  const temp = (vel.v + omega * change) * deltaTime;
  vel.v = (vel.v - omega * temp) * exp;
  let output = target + (change + temp) * exp;
  if (originalTo - current > 0 === output > originalTo) {
    output = originalTo;
    vel.v = (output - originalTo) / deltaTime;
  }
  return output;
}

/** Unity Vector2.SmoothDamp. `vel` is mutated. */
export function smoothDampV(current: V2, target: V2, vel: V2, smoothTime: number, maxSpeed = Infinity, deltaTime = FIXED_DT): V2 {
  smoothTime = Math.max(0.0001, smoothTime);
  const omega = 2 / smoothTime;
  const x = omega * deltaTime;
  const exp = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);
  let cx = current.x - target.x;
  let cy = current.y - target.y;
  const ox = target.x, oy = target.y;
  const maxChange = maxSpeed * smoothTime;
  const maxChangeSq = maxChange * maxChange;
  const sq = cx * cx + cy * cy;
  if (sq > maxChangeSq) {
    const mag = Math.sqrt(sq);
    cx = (cx / mag) * maxChange;
    cy = (cy / mag) * maxChange;
  }
  const tx = current.x - cx, ty = current.y - cy;
  const tempX = (vel.x + omega * cx) * deltaTime;
  const tempY = (vel.y + omega * cy) * deltaTime;
  vel.x = (vel.x - omega * tempX) * exp;
  vel.y = (vel.y - omega * tempY) * exp;
  let outX = tx + (cx + tempX) * exp;
  let outY = ty + (cy + tempY) * exp;
  const origMinusCurX = ox - current.x, origMinusCurY = oy - current.y;
  const outMinusOrigX = outX - ox, outMinusOrigY = outY - oy;
  if (origMinusCurX * outMinusOrigX + origMinusCurY * outMinusOrigY > 0) {
    outX = ox;
    outY = oy;
    vel.x = (outX - ox) / deltaTime;
    vel.y = (outY - oy) / deltaTime;
  }
  return { x: outX, y: outY };
}

/** Mathf.RoundToInt (banker's rounding like .NET) */
export function roundToInt(v: number): number {
  const r = Math.round(v);
  if (Math.abs(v % 1) === 0.5 && r % 2 !== 0) return r - 1;
  return r;
}

export enum FrameCounterRange {
  Linear,
  SineBounce,
}

/** Port of the game's FrameCounter (fixed 1/120 s frames). */
export class FrameCounter {
  frames: number;
  counter = 0;
  constructor(frames = 0) {
    this.frames = Math.max(0, frames | 0);
  }
  static seconds(s: number) {
    return new FrameCounter(roundToInt(Math.max(0, s) / FIXED_DT));
  }
  /** from serialized Unity data ({_seconds, _frames}) */
  static from(d: any, fallbackSeconds = 0): FrameCounter {
    if (d && d._frames !== undefined) return new FrameCounter(Number(d._frames) || 0);
    if (d && d._seconds !== undefined) return FrameCounter.seconds(Number(d._seconds) || 0);
    return FrameCounter.seconds(fallbackSeconds);
  }
  get seconds() {
    return this.frames * FIXED_DT;
  }
  set seconds(s: number) {
    this.frames = roundToInt(Math.max(0, s) / FIXED_DT);
  }
  get percentage() {
    return clamp(this.counter / this.frames, 0, 1) || (this.counter > 0 && this.frames === 0 ? 1 : 0);
  }
  set percentage(p: number) {
    this.counter = Math.trunc(p * this.frames);
  }
  start() {
    if (this.counter === 0) this.counter = 1;
  }
  count() {
    if (this.counter > 0) this.counter++;
  }
  isRunningNotFinished() {
    return this.counter > 0 && this.counter <= this.frames;
  }
  isNotRunning() {
    return this.counter === 0;
  }
  isFinished() {
    return this.counter > this.frames;
  }
  reset() {
    this.counter = 0;
  }
  restart() {
    this.counter = 1;
  }
  markFinished() {
    this.counter = this.frames + 1;
  }
  countCheckFinishReset(): boolean {
    this.count();
    if (this.isFinished()) {
      this.reset();
      return true;
    }
    return false;
  }
  range(start: number, stop: number, type = FrameCounterRange.Linear, percentageOverride = -1): number {
    let p = percentageOverride >= 0 ? percentageOverride : this.percentage;
    p = clamp01(p);
    if (type === FrameCounterRange.SineBounce) return Math.sin(2 * Math.PI * p - Math.PI * 0.5) * (1 - p);
    return (stop - start) * p + start;
  }
  copy() {
    const f = new FrameCounter(this.frames);
    f.counter = this.counter;
    return f;
  }
}

export enum PrimaryWaysToDie {
  Spikes,
  Laser,
  DeathZone,
  Dysentery,
  KillActivator,
  Projectile,
  Squish,
  String,
  TheGuardians,
}

export interface Deathod {
  primary: PrimaryWaysToDie;
  secondary?: string;
}

/** Unity Color as rgba 0..1 */
export type RGBA = [number, number, number, number];

/** Mathf.PingPong */
export function pingPong(t: number, length: number): number {
  if (length <= 0) return 0;
  const l2 = length * 2;
  const m = ((t % l2) + l2) % l2;
  return length - Math.abs(m - length);
}

/** Deterministic PRNG (mulberry32) for anything random in the sim. */
export function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
